import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
/** Styles Import */
import "./Style.css";
/** Services Import */
import Global from "../../Services/Common/Global";
import Commons from "../../Services/Common/Common";
/** Redux Import */
import { useSelector } from "react-redux";
/** Components Import */
import { Card, ProgressBar, Button } from "react-bootstrap";
import VideoPlayer from "../../Components/VideoPlayer";
import Swal from "../../Components/SweetAlert2";
import AutoTranslate from "../../Components/AutoTranslate";
/** Icons Import */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faCheck,
  faForward,
  faPlay,
  faPause,
} from "@fortawesome/free-solid-svg-icons";
import PDFViewerWithSumit from "./PDFViewerWithSumit";

const IS_IOS = /iphone|ipad|ipod/.test(
  window.navigator.userAgent.toLowerCase()
);

function WatchEducation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [dataList, setDataList] = useState([]); // 초기값을 빈 배열로 설정
  const [selectedData, setSelectedData] = useState(null);
  const [totalPercent, setTotalPercent] = useState(0);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const playerRef = useRef(null);
  const [shouldSeekToStart, setShouldSeekToStart] = useState(false);
  const [showSwal, setShowSwal] = useState(false);
  const [seekTarget, setSeekTarget] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const isPdf = selectedData?.content?.contentType?.toLowerCase() === "pdf";
  const isMobileDevice = Commons.IsMobile();
  const viewerHeight = "auto"; // 모든 모드에서 비율 유지
  const [pdfInitialPage, setPdfInitialPage] = useState(1);
  const [pdfNav, setPdfNav] = useState(null);
  const [showPlayPauseIcon, setShowPlayPauseIcon] = useState(false);
  const [playPauseIcon, setPlayPauseIcon] = useState("play");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [finalVideoSrc, setFinalVideoSrc] = useState(null); // ◀◀ [추가] 1. 최종 비디오 URL 상태
  const [isCheckingSrc, setIsCheckingSrc] = useState(false); // ◀◀ [추가] 2. 비디오 소스 확인 중 로딩 상태

  const language = useSelector((state) => state.localStorage.language);
  const authorization = useSelector(
    (state) => state.localStorage.authorization
  );
  const videoContainerRef = useRef(null);
  const initCalledRef = useRef(false);
  const sentEndRef = useRef(false);
  const actualPlayTimeRef = useRef(0); // 실제 재생 시간 추적 (일시정지 시간 제외)
  const lastPlayTimeRef = useRef(0); // 마지막 재생 시간
  const isPlayingRef = useRef(false); // 현재 재생 중인지 여부
  const playStartTimeRef = useRef(null); // 재생 시작 시점의 타임스탬프
  const currentEduIdRef = useRef(null); // 현재 재생 중인 eduId (이전 영상의 handleProgress 차단용)

  const isCompleted =
    selectedData && selectedData.viewedValue === selectedData.totalValue;

  useEffect(() => {
    if (!initCalledRef.current) {
      initCalledRef.current = true;
      Init();
    }
  }, []);

  useEffect(() => {
    if (!authorization.auth) {
      navigate("/");
    }
  }, [authorization.auth]);

  useEffect(() => {
    if (
      !isPdf &&
      selectedData &&
      typeof selectedData.viewedValue === "number" &&
      selectedData.viewedValue > 1 &&
      selectedData.viewedValue < selectedData.totalValue &&
      !shouldSeekToStart
    ) {
      setShowSwal(true);
      const min = Math.floor(selectedData.viewedValue / 60);
      const sec = selectedData.viewedValue % 60;

      // 모달이 표시되는 동안 재생되지 않도록 seekTarget을 null로 설정
      setSeekTarget(null);

      Swal.custom({
        title: `${min}:${
          sec < 10 ? "0" : ""
        }${sec}에서 이어서 재생하시겠습니까?`,
        showCancelButton: true,
        confirmButtonText: "예",
        cancelButtonText: "아니오(처음부터)",
      }).then((result) => {
        setShowSwal(false);

        if (result.isConfirmed) {
          console.log(
            "[WatchEducation] User chose to resume from:",
            selectedData.viewedValue
          );
          // 플레이어가 준비될 때까지 대기 후 강제로 이동
          const seekAndPlay = () => {
            if (playerRef.current) {
              console.log(
                "[WatchEducation] Seeking to:",
                selectedData.viewedValue
              );
              playerRef.current.currentTime(selectedData.viewedValue);
              playerRef.current
                .play()
                .catch((err) =>
                  console.log("[WatchEducation] Play error:", err)
                );
            } else {
              // 플레이어가 아직 준비되지 않았으면 다시 시도
              setTimeout(seekAndPlay, 100);
            }
          };
          seekAndPlay();
        } else {
          console.log("[WatchEducation] User chose to start from beginning");
          setSelectedData(prevData => ({ ...prevData, viewedValue: 0 }));
          setShouldSeekToStart(true);
          // 처음부터 재생
        }
      });
    }
  }, [selectedData, shouldSeekToStart, isPdf, playerRef]);

  useEffect(() => {
    if (
      isPdf &&
      selectedData &&
      typeof selectedData.viewedValue === "number" &&
      selectedData.viewedValue > 0 &&
      selectedData.viewedValue < selectedData.totalValue &&
      !shouldSeekToStart
    ) {
      setShowSwal(true);
      Swal.custom({
        title: `${selectedData.viewedValue}번째 페이지부터 이어서 보시겠습니까?`,
        showCancelButton: true,
        confirmButtonText: "예",
        cancelButtonText: "아니오(처음부터)",
      }).then((result) => {
        setShowSwal(false);
        if (result.isConfirmed) {
          setPdfInitialPage(selectedData.viewedValue);
        } else {
          setPdfInitialPage(1);
        }
      });
    }
  }, [isPdf, selectedData, shouldSeekToStart]);

  useEffect(() => {
    setPdfInitialPage(1);
  }, [selectedData]);

  useEffect(() => {
    if (selectedData && typeof selectedData.viewedValue === "number") {
      if (shouldSeekToStart) {
        setSeekTarget(0);
      } else if (
        selectedData.viewedValue > 0 &&
        selectedData.viewedValue < selectedData.totalValue
      ) {
        setSeekTarget(selectedData.viewedValue);
      } else {
        setSeekTarget(0);
      }
    }
  }, [selectedData, shouldSeekToStart]);

  useEffect(() => {
    if (shouldSeekToStart && seekTarget === 0 && playerRef.current) {
      console.log("[WatchEducation] Manually playing from start.");
      
      playerRef.current.currentTime(0);
      playerRef.current
        .play()
        .catch((err) => console.log("[WatchEducation] Play error:", err));
      
      setShouldSeekToStart(false); 
    }
    
  }, [shouldSeekToStart, seekTarget, playerRef.current]);

  useEffect(() => {
    return () => {
      Swal.close();
      Commons.cleanupMediaElements();
    };
  }, []);

  const Search = async () => {
    try {
      const result = await Commons.Axios(
        "get",
        `${Global.base.useUrl + Global.urls.Content.EduViewedList}?siteId=${
          location.state.site.siteId
        }&companyCode=${location.state.company.companyCode}&id=${
          location.state.site.id
        }`
      );
      let data = result.data;
      if (!data || data.type === "Error") {
        throw new Error(data.message || data);
      }
      if (data == "데이터가 없습니다.") {
        navigate(-1);
        Swal.custom({
          title: "등록된 교육 영상이 없습니다.",
          text: "관리자에게 문의하세요.",
          icon: "warning",
          confirmButtonText: "확인",
        });
      } else {
        data.sort((a, b) => a.content.seq - b.content.seq);
        data = data.map((item) => ({
          ...item,
          viewedValue:
            typeof item.viewedValue === "number" && item.viewedValue <= 1
              ? 0
              : item.viewedValue,
        }));
        const totalValue = data.reduce(
          (sum, item) => sum + (item.totalValue || 0),
          0
        );
        const viewedValue = data.reduce(
          (sum, item) => sum + (item.viewedValue || 0),
          0
        );
        const progressPercent =
          Math.floor((viewedValue / totalValue) * 100) || 0;
        setTotalPercent(progressPercent);
        setDataList(Commons.InsertNumber(data));
      }
    } catch (e) {
      console.log("Search Error: ", e);
    }
  };

  const Init = async () => {
    try {
      const result = await Commons.Axios(
        "get",
        `${Global.base.useUrl + Global.urls.Content.EduViewedList}?siteId=${
          location.state.site.siteId
        }&companyCode=${location.state.company.companyCode}&id=${
          location.state.site.id
        }`
      );
      let data = result.data;
      console.log("Init Data: ",data);
      if (!data || data.type === "Error") {
        throw new Error(data.message || data);
      }
      if (data == "데이터가 없습니다.") {
        navigate(-1);
        Swal.custom({
          title: "등록된 교육 영상이 없습니다.",
          text: "관리자에게 문의하세요.",
          icon: "warning",
          confirmButtonText: "확인",
        });
      } else {
        data.sort((a, b) => a.content.seq - b.content.seq);
        data = data.map((item) => ({
          ...item,
          viewedValue:
            typeof item.viewedValue === "number" && item.viewedValue <= 1
              ? 0
              : item.viewedValue,
        }));
        const totalValue = data.reduce(
          (sum, item) => sum + (item.totalValue || 0),
          0
        );
        const viewedValue = data.reduce(
          (sum, item) => sum + (item.viewedValue || 0),
          0
        );
        const progressPercent =
          Math.floor((viewedValue / totalValue) * 100) || 0;
        setTotalPercent(progressPercent);
        setDataList(Commons.InsertNumber(data));
        const firstIncomplete = data.find(
          (item) => item.viewedValue !== item.totalValue
        );
        if (firstIncomplete) {
          setSelectedData(firstIncomplete);
        } else if (data.length > 0) {
          setSelectedData(data[0]);
        }
      }
    } catch (e) {
      console.log("Init Error: ", e);
    }
  };

  const Sumit = async (seconds, id, eduid) => {
    try {
      await Commons.Axios(
        "post",
        Global.base.useUrl + Global.urls.Content.EduViewedUpdate,
        { id: id, eduid: eduid, seconds: seconds },
        {}
      );
    } catch (e) {
      console.log("Sumit Error:", e);
    }
  };

  const handleProgress = (playedSeconds) => {
    // 다른 영상으로 전환된 경우 이전 영상의 handleProgress 콜백을 무시
    if (selectedData && currentEduIdRef.current !== selectedData.eduId) {
      console.log("[Progress] 다른 영상으로 전환되어 이전 영상의 handleProgress 무시:", {
        currentEduId: currentEduIdRef.current,
        selectedEduId: selectedData?.eduId
      });
      return;
    }
    
    setPlayedSeconds(playedSeconds);
    const flooredSeconds = Math.floor(playedSeconds);
    
    // 실제 재생 시간 업데이트 (재생 중일 때만)
    if (isPlayingRef.current && playStartTimeRef.current) {
      const now = Date.now();
      const elapsed = (now - playStartTimeRef.current) / 1000; // 초 단위
      // 현재 비디오 시간과 마지막 저장된 시간을 비교하여 탐색 감지
      const currentVideoTime = playedSeconds;
      const lastSavedVideoTime = lastPlayTimeRef.current;
      
      // 탐색이 발생한 경우 (시간이 갑자기 변한 경우)
      if (Math.abs(currentVideoTime - lastSavedVideoTime - elapsed) > 2) {
        // 탐색 발생: 재생 시간을 현재 위치 기준으로 재설정
        lastPlayTimeRef.current = currentVideoTime;
        playStartTimeRef.current = now;
        actualPlayTimeRef.current = currentVideoTime;
      } else {
        // 정상 재생: 누적 시간 계산
        actualPlayTimeRef.current = lastPlayTimeRef.current + elapsed;
      }
    }
    
    // 완료되지 않은 영상만 진행률 저장 (5초마다)
    if (
      !sentEndRef.current &&
      Math.floor(playedSeconds) % 5 === 0 &&
      playedSeconds > 0 &&
      selectedData
    ) {
      if (
        flooredSeconds !== selectedData.viewedValue &&
        selectedData.viewedValue !== selectedData.totalValue
      ) {
        console.log(
          "[Progress] Saving progress:",
          flooredSeconds,
          "seconds"
        );
        Sumit(flooredSeconds, selectedData.id, selectedData.eduId);
      }
    }
    
    // 실제 재생 시간 기준 완료 처리 (duration의 95% 이상 재생)
    // 주의: 이 로직은 실제로 재생된 시간을 기준으로 하므로 탭 전환으로 인한 오작동 방지
    if (
      videoDuration &&
      videoDuration > 0 &&
      !sentEndRef.current &&
      selectedData &&
      selectedData.viewedValue !== selectedData.totalValue
    ) {
      const playRatio = actualPlayTimeRef.current / videoDuration;
      const currentTimeRatio = playedSeconds / videoDuration;
      
      // 실제 재생 시간이 95% 이상이거나, 현재 재생 시간이 98% 이상일 때 완료 처리
      // (실제 재생 시간이 더 정확하지만, 현재 재생 시간도 보조적으로 확인)
      if (playRatio >= 0.95 || (currentTimeRatio >= 0.98 && actualPlayTimeRef.current >= videoDuration * 0.85)) {
        console.log("[Progress] 영상 완료 처리 (실제 재생 시간 기준):", {
          actualPlayTime: actualPlayTimeRef.current,
          videoDuration: videoDuration,
          playRatio: (playRatio * 100).toFixed(2) + "%",
          currentTime: playedSeconds,
          currentTimeRatio: (currentTimeRatio * 100).toFixed(2) + "%"
        });
        Sumit(selectedData.totalValue + 10, selectedData.id, selectedData.eduId);
        sentEndRef.current = true;
      }
    }
  };

  const handleDuration = (duration) => {
    // 다른 영상으로 전환된 경우 이전 영상의 handleDuration 콜백을 무시
    if (selectedData && currentEduIdRef.current !== selectedData.eduId) {
      console.log("[Duration] 다른 영상으로 전환되어 이전 영상의 handleDuration 무시:", {
        currentEduId: currentEduIdRef.current,
        selectedEduId: selectedData?.eduId
      });
      return;
    }
    
    setVideoDuration(duration);
    sentEndRef.current = false;
    // duration이 변경되면 실제 재생 시간 초기화 (새로운 비디오 로드 시)
    // 단, 재생 중이 아니거나 초기 상태일 때만 초기화
    if (!isPlayingRef.current) {
      actualPlayTimeRef.current = 0;
      lastPlayTimeRef.current = 0;
      playStartTimeRef.current = null;
    }
  };

  const HandleVideoEnd = async () => {
    // 다른 영상으로 전환된 경우 이전 영상의 HandleVideoEnd 콜백을 무시
    if (selectedData && currentEduIdRef.current !== selectedData.eduId) {
      console.log("[VideoEnd] 다른 영상으로 전환되어 이전 영상의 HandleVideoEnd 무시:", {
        currentEduId: currentEduIdRef.current,
        selectedEduId: selectedData?.eduId
      });
      return;
    }
    
    if (selectedData && selectedData.viewedValue === selectedData.totalValue) {
      return;
    }
    
    // 실제 재생 시간 확인 (뒤로가기 후 돌아온 경우 방지)
    // 실제 재생 시간이 duration의 80% 이상일 때만 완료 처리
    const minRequiredPlayRatio = 0.80; // 최소 80% 재생 필요
    
    if (
      !sentEndRef.current &&
      selectedData &&
      selectedData.eduId &&
      selectedData.viewedValue !== selectedData.totalValue &&
      videoDuration > 0
    ) {
      const playRatio = actualPlayTimeRef.current / videoDuration;
      
      // 실제 재생 시간이 충분할 때만 완료 처리
      if (playRatio >= minRequiredPlayRatio) {
        console.log("[VideoEnd] 영상 완료 처리 (ended 이벤트, 실제 재생 시간 확인):", {
          actualPlayTime: actualPlayTimeRef.current,
          videoDuration: videoDuration,
          playRatio: (playRatio * 100).toFixed(2) + "%"
        });
        await Sumit(selectedData.totalValue + 10, selectedData.id, selectedData.eduId);
        sentEndRef.current = true;
      } else {
        console.log("[VideoEnd] 실제 재생 시간 부족으로 완료 처리 건너뜀:", {
          actualPlayTime: actualPlayTimeRef.current,
          videoDuration: videoDuration,
          playRatio: (playRatio * 100).toFixed(2) + "%",
          required: (minRequiredPlayRatio * 100).toFixed(0) + "%"
        });
        // 완료 처리하지 않고 진행률만 업데이트
        if (actualPlayTimeRef.current > 0) {
          await Sumit(Math.floor(actualPlayTimeRef.current), selectedData.id, selectedData.eduId);
        }
        return; // 완료 처리를 하지 않고 종료
      }
    } else if (!sentEndRef.current && videoDuration === 0) {
      // duration이 설정되지 않은 경우 (초기 로딩 중 등)는 기존 로직 사용
      console.log("[VideoEnd] Duration이 없어 기존 로직 사용");
      if (selectedData && selectedData.eduId && selectedData.viewedValue !== selectedData.totalValue) {
        await Sumit(selectedData.totalValue + 10, selectedData.id, selectedData.eduId);
        sentEndRef.current = true;
      }
    }
    
    const allCompleted = dataList.every(
      (item) =>
        item.viewedValue === item.totalValue ||
        item.eduId === selectedData.eduId
    );
    if (allCompleted) {
      Swal.custom({
        title: "모든 교육을 이수하였습니다",
        icon: "success",
        confirmButtonText: "확인",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate(-1);
        }
      });
    } else {
      window.location.reload();
    }
  };

  const getSubtitleLabel = (langCode) => {
    const labels = {
      'ko': '한국어',
      'en': 'English',
      'vi': 'Tiếng Việt',
      'bn': 'বাংলা',
      'pt': 'Português',
      'zh': '中文 (简体)', // 중국어 간체
      'de': 'Deutsch',
      'es': 'Español',
      'fr': 'Français',
      'id': 'Bahasa Indonesia',
      'hi': 'हिन्दी',
      'it': 'Italiano',
      'ja': '日本語',
      'si': 'සිංහල',
      'mn': 'Монгол',
      'ur': 'اردو',
      'ru': 'Русский',
      'th': 'ไทย',
      'uz': 'Oʻzbekcha',
    };
    // labels 객체에 정의된 이름이 있으면 그 이름을 반환하고,
    // 없으면 기본값으로 언어 코드를 대문자로 변환하여 반환합니다.
    return labels[langCode] || langCode.toUpperCase();
  };

  const getSubtitleTracks = () => {
    if (!selectedData?.content) return []; // selectedData를 직접 사용하도록 수정
    const { id, title } = selectedData.content;
    if (title === "일성건설 신규 근로자 교육") {
      switch (language.lang) {
        case "vi": // 베트남어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-vi.vtt",
              srcLang: "vi",
              label: getSubtitleLabel("vi"),
              default: false,
            },
          ];
        case "bn": // 벵골어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-bn.vtt",
              srcLang: "bn",
              label: getSubtitleLabel("bn"),
              default: false,
            },
          ];
        case "pt": // 포르투갈어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-pt.vtt",
              srcLang: "pt",
              label: getSubtitleLabel("pt"),
              default: false,
            },
          ];
        // case 'fr-CA': // 캐나다 프랑스어 (파일 없음)
        case "zh": // 중국어(간체)
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-zh.vtt", // zh-CN 대신 zh 사용 가정
              srcLang: "zh",
              label: getSubtitleLabel("zh"),
              default: false,
            },
          ];
        case "de": // 독일어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-de.vtt",
              srcLang: "de",
              label: getSubtitleLabel("de"),
              default: false,
            },
          ];
        case "es": // 스페인어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-es.vtt",
              srcLang: "es",
              label: getSubtitleLabel("es"),
              default: false,
            },
          ];
        case "fr": // 프랑스어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-fr.vtt",
              srcLang: "fr",
              label: getSubtitleLabel("fr"),
              default: false,
            },
          ];
        case "id": // 인도네시아어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-id.vtt",
              srcLang: "id",
              label: getSubtitleLabel("id"),
              default: false,
            },
          ];
        case "hi": // 힌디어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-hi.vtt",
              srcLang: "hi",
              label: getSubtitleLabel("hi"),
              default: false,
            },
          ];
        case "it": // 이탈리아어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-it.vtt",
              srcLang: "it",
              label: getSubtitleLabel("it"),
              default: false,
            },
          ];
        case "ja": // 일본어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-ja.vtt",
              srcLang: "ja",
              label: getSubtitleLabel("ja"),
              default: false,
            },
          ];
        case "ko": // 한국어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-ko-KR.vtt", // 파일명에 -KR 포함 확인
              srcLang: "ko",
              label: getSubtitleLabel("ko"),
              default: false,
            },
          ];
        case "si": // 싱할라어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-si.vtt",
              srcLang: "si",
              label: getSubtitleLabel("si"),
              default: false,
            },
          ];
        case "mn": // 몽골어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-mn.vtt",
              srcLang: "mn",
              label: getSubtitleLabel("mn"),
              default: false,
            },
          ];
        case "ur": // 우르두어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-ur.vtt",
              srcLang: "ur",
              label: getSubtitleLabel("ur"),
              default: false,
            },
          ];
        case "ru": // 러시아어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-ru.vtt",
              srcLang: "ru",
              label: getSubtitleLabel("ru"),
              default: false,
            },
          ];
        case "th": // 태국어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-th.vtt",
              srcLang: "th",
              label: getSubtitleLabel("th"),
              default: false,
            },
          ];
        case "uz": // 우즈베크어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-uz.vtt",
              srcLang: "uz",
              label: getSubtitleLabel("uz"),
              default: false,
            },
          ];
        // case 'en': // 영어는 default로 처리됨
        default: // 영어 포함 나머지 모든 언어
          return [
            {
              kind: "subtitles",
              src: "/subtitles/ilsungEdu-realtime-01-en.vtt",
              srcLang: "en",
              label: getSubtitleLabel("en"),
              default: true, // 기본값
            },
          ];
      }
    }
    if (!selectedData.content.hasSubtitle) {
      return []; // hasSubtitle이 false면 자막 없음
    }

    // 원본 비디오 파일 URL 가져오기
    const originalVideoUrl = selectedData.content.content;
    if (!originalVideoUrl || typeof originalVideoUrl !== 'string') {
      return [];
    }
    
    // 자막 기본 경로
    const subtitleBasePath = 'https://d5czkublj4fg0.cloudfront.net/Subtitles/';

    // 비디오 URL에서 확장자를 제외한 "파일 이름" 추출
    const filenameWithExt = originalVideoUrl.split('/').pop(); 
    const filenameBase = filenameWithExt.split('.').slice(0, -1).join('.');

    if (!filenameBase) {
      console.error("[Subtitles] 비디오 URL에서 파일명을 추출할 수 없습니다:", originalVideoUrl);
      return [];
    }

    const subtitleSrc = `${subtitleBasePath}${filenameBase}/${filenameBase}-${language.lang}.vtt`;

    const subtitleLabel = getSubtitleLabel(language.lang);

    console.log(`[Subtitles] 동적 자막 파일 로드 시도: ${subtitleSrc}`);

    // VideoPlayer가 요구하는 형식으로 반환
    return [{
      kind: 'subtitles',
      src: subtitleSrc,
      srcLang: language.lang,
      label: subtitleLabel,
      default: true 
    }];

    // // 22번 콘텐츠 '안방예능 11'의 자막
    // if (title === '안방예능 11') {
    //   return [
    //     {
    //       kind: 'subtitles',
    //       src: '/subtitles/TranscribeTest.vtt', // 👈 아래 '중요' 설명 참고
    //       srcLang: 'ko',
    //       label: '한국어',
    //       default: true,
    //     },
    //   ];
    // }

    // // 10번 콘텐츠 '추락'의 자막
    // if (title === '추락') {
    //   return [
    //     {
    //       kind: 'subtitles',
    //       src: '/subtitles/vttSubtitles.vtt', // 👈 아래 '중요' 설명 참고
    //       srcLang: 'ko',
    //       label: '한국어',
    //       default: true,
    //     },
    //   ];
    // }

    // // 11번 콘텐츠 '개인보호구'의 자막
    // if (title === '개인보호구') {
    //   return [
    //     {
    //       kind: 'subtitles',
    //       src: '/subtitles/20250704142256_dfb4c02a9f964462a0ea8c386ea6b301.vtt', // 👈 아래 '중요' 설명 참고
    //       srcLang: 'ko',
    //       label: '한국어',
    //       default: true,
    //     },
    //   ];
    // }
    // // 위 조건에 맞는 자막이 없는 경우, 빈 배열을 반환

    return [];
  };

useEffect(() => {
  // 선택된 데이터가 없으면 src를 null로 설정
  if (!selectedData) {
    setFinalVideoSrc(null);
    return;
  }

  sentEndRef.current = false;
  // selectedData가 변경되면 실제 재생 시간 초기화
  actualPlayTimeRef.current = 0;
  lastPlayTimeRef.current = 0;
  isPlayingRef.current = false;
  playStartTimeRef.current = null;
  // 현재 eduId 업데이트 (handleProgress에서 이전 영상의 콜백 차단용)
  if (selectedData?.eduId) {
    currentEduIdRef.current = selectedData.eduId;
  }

  // PDF인 경우, PDF 경로를 src로 설정 (VideoPlayer가 렌더링되진 않음)
  if (isPdf) {
    setFinalVideoSrc(selectedData.content.content);
    return;
  }

  // --- 비디오 소스 확인 시작 ---
  setIsCheckingSrc(true); // 로딩 상태 시작
  
  // 1단계: 언어에 따른 기본 소스 결정 (기존 getVideoSource 로직 통합)
  let baseSrc = selectedData.content.content; // 기본 원본 소스
  const title = selectedData.content.title;

  if (title === "일성건설 신규 근로자 교육") { 
    if (language.lang === 'vi') {
      baseSrc = 'https://d5czkublj4fg0.cloudfront.net/Education/20251031/일성베트남/일성베트남.m3u8';
    }
  }
  // (다른 언어/콘텐츠 규칙이 있다면 여기에 추가)

  
  // 2단계: baseSrc가 .mp4 또는 .mov인지 확인
  if (baseSrc && (baseSrc.endsWith('.mp4') || baseSrc.endsWith('.mov'))) {
    
    // .m3u8 경로 생성 로직 ( .../파일명/파일명.m3u8 )
    const urlParts = baseSrc.split('/');
    const filenameWithExt = urlParts.pop(); // '파일명.mp4'
    const basePath = urlParts.join('/'); // 'https.../경로'
    // 확장자를 제외한 파일명 (예: 'my.video.mp4' -> 'my.video')
    const filename = filenameWithExt.split('.').slice(0, -1).join('.'); 
    
    const m3u8Url = `${basePath}/${filename}/${filename}.m3u8`;
    
    console.log("[WatchEducation] .m3u8 버전 확인 시도:", m3u8Url);

    // 3단계: .m3u8 URL이 유효한지 비동기 체크 (HEAD 요청)
    const checkUrl = async () => {
      try {
        // HEAD 요청으로 파일 존재 여부만 가볍게 확인
        const response = await fetch(m3u8Url, { method: 'HEAD' });
        
        if (response.ok) {
          // .m3u8 파일 존재! -> 최종 소스를 .m3u8로 설정
          console.log("[WatchEducation] .m3u8 버전 발견. 스트리밍으로 재생합니다:", m3u8Url);
          setFinalVideoSrc(m3u8Url);
        } else {
          // .m3u8 파일 없음 -> 원본 .mp4/.mov 소스로 설정
          console.log("[WatchEducation] .m3u8 버전을 찾을 수 없음. 원본 파일로 재생합니다:", baseSrc);
          setFinalVideoSrc(baseSrc);
        }
      } catch (e) {
        // 네트워크 오류, CORS 오류 등 -> 안전하게 원본 소스로 설정
        console.warn("[WatchEducation] .m3u8 확인 중 오류 발생. 원본 파일로 재생합니다:", e.message);
        setFinalVideoSrc(baseSrc);
      } finally {
        setIsCheckingSrc(false); // 로딩 상태 종료
      }
    };
    
    checkUrl(); // 비동기 체크 실행

  } else {
    // 4단계: .mp4/.mov가 아님 (아마도 이미 .m3u8이거나 다른 형식) -> baseSrc를 그대로 최종 소스로 사용
    console.log("[WatchEducation] .mp4/.mov가 아니므로 원본 소스를 그대로 사용합니다:", baseSrc);
    setFinalVideoSrc(baseSrc);
    setIsCheckingSrc(false); // 로딩 상태 종료
  }

}, [selectedData, isPdf, language.lang]); // ◀◀ [추가] selectedData, isPdf, language.lang이 바뀔 때마다 실행

  const subtitleTracks = useMemo(() => {
    console.log("[WatchEducation] 자막 목록을 새로 계산합니다."); // ◀◀ 이 로그는 비디오가 바뀔 때만 찍힐 것입니다.
    return getSubtitleTracks();
  }, [selectedData, language.lang]);

  const hasSubtitles = subtitleTracks.length > 0;

  return (
    <div className="education-page">
      <div className={`layout`}>
        <div className="edu-watch-video-container">
          <div
            ref={videoContainerRef}
            className="edu-watch-video-container-video"
            style={{
              height: viewerHeight,
              maxWidth: "100vw",
              overflowX: "auto",
              position: "relative",
            }}
          >
            {isPdf ? (
              <PDFViewerWithSumit
                key={selectedData?.eduId + "-" + pdfInitialPage}
                fileUrl={selectedData?.content?.content}
                selectedData={selectedData}
                Sumit={(page, id, eduid) => {
                  if (
                    selectedData &&
                    selectedData.viewedValue === selectedData.totalValue
                  ) {
                    return Promise.resolve();
                  }
                  return Sumit(page, id, eduid);
                }}
                initialPage={pdfInitialPage}
                onPageChangeNav={(nav) => setPdfNav(nav)}
                dataList={dataList}
                setSelectedData={setSelectedData}
                navigate={navigate}
                onAfterComplete={Search}
              />
            ) : selectedData &&
              selectedData.eduId &&
              selectedData.content?.content ? (
              <>
              {(isCheckingSrc || !finalVideoSrc) ? (
                  // 비디오 소스를 확인 중이거나, 아직 최종 URL이 설정되지 않았으면 로더 표시
                  <div style={{
                    width: '100%',
                    height: '80vh', // 기존 비디오 높이와 유사하게
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#000',
                    color: '#fff',
                    fontSize: '1.2rem'
                  }}>
                    비디오 소스를 확인하는 중입니다...
                  </div>
                ) : (
                  // 확인 완료: finalVideoSrc로 VideoPlayer 렌더링
                  <VideoPlayer
                    key={selectedData.eduId + (finalVideoSrc || '')} // ◀◀ [수정] src 변경 시 플레이어 강제 리셋을 위해 key 변경
                    src={finalVideoSrc} // ◀◀ [수정] finalVideoSrc 사용
                    subtitles={subtitleTracks}
                    onProgress={handleProgress}
                    onDuration={handleDuration}
                    onEnded={HandleVideoEnd}
                    onReady={(player) => {
                      console.log(
                        "[WatchEducation] Video player ready, seekTarget:",
                        seekTarget,
                        "showSwal:",
                        showSwal
                      );
                      playerRef.current = player;
                    }}
                    onPlay={(fullscreen) => {
                      console.log(
                        "[WatchEducation] Play event, fullscreen:",
                        fullscreen
                      );
                      setIsFullscreen(fullscreen);
                      setPlayPauseIcon("play");
                      setShowPlayPauseIcon(true);
                      setTimeout(() => setShowPlayPauseIcon(false), 500);
                      
                      // 실제 재생 시간 추적 시작
                      if (!isPlayingRef.current) {
                        isPlayingRef.current = true;
                        const now = Date.now();
                        
                        // 재생 시작 시 현재 비디오 재생 위치 확인
                        if (playerRef.current) {
                          const currentTime = playerRef.current.currentTime() || 0;
                          
                          // 이전에 누적된 재생 시간이 없거나, 현재 시간이 마지막 저장 시간보다 크면 업데이트
                          // (탐색으로 인한 시간 변경 감지)
                          if (lastPlayTimeRef.current === 0 || currentTime >= lastPlayTimeRef.current) {
                            // 정상적인 재생: 현재 시간을 기준으로 시작
                            lastPlayTimeRef.current = currentTime;
                            actualPlayTimeRef.current = currentTime;
                          }
                          // 탐색으로 시간이 줄어든 경우는 handleProgress에서 처리
                        }
                        
                        playStartTimeRef.current = now;
                        
                        console.log("[WatchEducation] 재생 시작 - 실제 재생 시간 추적 시작:", {
                          lastPlayTime: lastPlayTimeRef.current,
                          actualPlayTime: actualPlayTimeRef.current,
                          currentVideoTime: playerRef.current?.currentTime() || 0,
                          startTime: playStartTimeRef.current
                        });
                      }
                    }}
                    onPause={(fullscreen) => {
                      console.log(
                        "[WatchEducation] Pause event, fullscreen:",
                        fullscreen
                      );
                      setIsFullscreen(fullscreen);
                      setPlayPauseIcon("pause");
                      setShowPlayPauseIcon(true);
                      setTimeout(() => setShowPlayPauseIcon(false), 500);
                      
                      // 실제 재생 시간 추적 중지 및 누적
                      if (isPlayingRef.current && playStartTimeRef.current) {
                        const now = Date.now();
                        const elapsed = (now - playStartTimeRef.current) / 1000; // 초 단위
                        lastPlayTimeRef.current = lastPlayTimeRef.current + elapsed;
                        actualPlayTimeRef.current = lastPlayTimeRef.current;
                        isPlayingRef.current = false;
                        playStartTimeRef.current = null;
                        console.log("[WatchEducation] 일시정지 - 실제 재생 시간 업데이트:", {
                          elapsed: elapsed.toFixed(2),
                          totalPlayTime: actualPlayTimeRef.current.toFixed(2)
                        });
                      }
                    }}
                    onFullscreenChange={(fullscreen) => {
                      console.log(
                        "[WatchEducation] Fullscreen changed to:",
                        fullscreen
                      );
                      setIsFullscreen(fullscreen);
                    }}
                    startTime={seekTarget !== null ? seekTarget : 0}
                    autoplay={!showSwal && seekTarget !== null && seekTarget >= 0}
                    volume={0.8}
                    isCompleted={isCompleted}
                    className="education-video-player"
                    style={{ width: "100%", height: "100%" }}
                  />
                )}
                
                {/* 재생/일시정지 애니메이션 (일반 모드) */}
                {showPlayPauseIcon && !isFullscreen && (
                  <div
                    className="play-pause-animation"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "120px",
                      height: "120px",
                      background: "rgba(0, 0, 0, 0.7)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10000,
                      pointerEvents: "none",
                      animation: "playPauseAnimation 0.5s ease-out",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={playPauseIcon === "play" ? faPlay : faPause}
                      style={{
                        fontSize: "3.5rem",
                        color: "#fff",
                        marginLeft: playPauseIcon === "play" ? "8px" : "0",
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "80vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#000",
                  color: "#fff",
                }}
              >
                {selectedData
                  ? "비디오를 로드하는 중..."
                  : "교육 콘텐츠를 선택해주세요"}
              </div>
            )}
          </div>

          <div className="my-2">
            <Card>
              <Card.Header
                className={`${
                  Commons.IsMobile() ? "flex-column" : "flex-between"
                }`}
              >
                <Card.Title
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 500,
                    marginTop: "0.5rem",
                    marginBottom: "-0.5rem",
                  }}
                >
                  <AutoTranslate targetLanguage={language.lang}>
                    {selectedData?.content?.title}
                  </AutoTranslate>
                </Card.Title>
                {Array.isArray(selectedData?.content?.tags) &&
                  selectedData?.content?.tags.length > 0 && (
                    <div
                      style={{
                        margin: "0.5rem 0 -0.5rem 0",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                      }}
                    >
                      {selectedData?.content?.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          style={{
                            color: "#43a0fe",
                            cursor: "pointer",
                            fontWeight: 500,
                          }}
                          onClick={() =>
                            navigate("/search-result", {
                              state: { data: { items: [] }, searchText: tag },
                            })
                          }
                        >
                          #
                          <AutoTranslate targetLanguage={language.lang}>
                            {tag}
                          </AutoTranslate>
                        </span>
                      ))}
                    </div>
                  )}
              </Card.Header>
              <Card.Body>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  <Card.Text>
                    <AutoTranslate targetLanguage={language.lang}>{`조회 수 ${
                      selectedData?.content?.viewCount
                        ? selectedData?.content?.viewCount
                        : 0
                    }회`}</AutoTranslate>
                  </Card.Text>
                  <span style={{ color: "#bbb", fontSize: "0.8rem" }}>|</span>
                  <Card.Text>
                    <AutoTranslate targetLanguage={language.lang}>{`${
                      selectedData?.content?.createdAt
                        ? selectedData?.content?.createdAt
                            .toString()
                            .slice(0, 10)
                        : ""
                    }`}</AutoTranslate>
                  </Card.Text>
                </div>
                <Card.Text>
                  <AutoTranslate targetLanguage={language.lang}>
                    {selectedData?.content?.summary}
                  </AutoTranslate>
                </Card.Text>
              </Card.Body>
            </Card>
          </div>
        </div>
        <Card className="edu-watch-list-container">
          <Card.Header
            style={{
              background: "#f8f9fa",
              borderBottom: "1px solid rgba(0,0,0,0.05)",
              padding: "1.5rem 1.5rem 1rem 1.5rem",
            }}
          >
            <Card.Title
              style={{
                fontWeight: "bold",
                fontSize: "1.3rem",
                color: "#2c3e50",
              }}
            >
              <AutoTranslate targetLanguage={language.lang}>
                교육 진행 사항
              </AutoTranslate>
            </Card.Title>
            <ProgressBar
              now={totalPercent}
              label={`${totalPercent}%`}
              variant="info"
              style={{ marginTop: 8 }}
            />
          </Card.Header>
          <Card.Body
            className="edu-watch-list-content"
            style={{ padding: "1.5rem", background: "#fff" }}
          >
            {/* ▼▼▼ [수정 4] .map 에러 방지를 위한 가드 추가 ▼▼▼ */}
            {Array.isArray(dataList) &&
              dataList.map((item, index) => {
                const isCompleted = item.viewedValue === item.totalValue;
                const isCurrent = item.eduId === selectedData?.eduId;
                const isClickable = true; // 모든 콘텐츠 클릭 가능
                let cardColor = "#fff";
                let borderColor = "#03bbfe";
                let icon = faPlay;
                let statusText = (
                  <AutoTranslate targetLanguage={language.lang}>
                    교육 시작
                  </AutoTranslate>
                );
                let statusColor = "#03bbfe";
                if (isCompleted) {
                  cardColor = "#d4edda";
                  borderColor = "#198754";
                  icon = faCheck;
                  statusText = (
                    <AutoTranslate targetLanguage={language.lang}>
                      교육 완료
                    </AutoTranslate>
                  );
                  statusColor = "#198754";
                } else if (item.viewedValue > 0) {
                  cardColor = "#f8f9fa";
                  borderColor = "#03bbfe";
                  icon = faForward;
                  statusText = (
                    <AutoTranslate targetLanguage={language.lang}>
                      이어 보기
                    </AutoTranslate>
                  );
                  statusColor = "#03bbfe";
                } else {
                  cardColor = "#f8f9fa";
                  borderColor = "#03bbfe";
                  icon = faPlay;
                  statusText = (
                    <AutoTranslate targetLanguage={language.lang}>
                      교육 시작
                    </AutoTranslate>
                  );
                  statusColor = "#03bbfe";
                }

                let progress = 0;
                if (item.totalValue > 0) {
                  if (isCurrent) {
                    const isPdfCard =
                      selectedData?.content?.contentType?.toLowerCase() ===
                      "pdf";
                    if (isPdfCard) {
                      const v =
                        typeof pdfNav === "number" && pdfNav > 0
                          ? pdfNav
                          : item.viewedValue;
                      progress = v >= 2 ? Math.min(1, v / item.totalValue) : 0;
                    } else if (videoDuration > 0) {
                      progress = Math.min(1, playedSeconds / videoDuration);
                    } else {
                      progress = Math.min(
                        1,
                        item.viewedValue / item.totalValue
                      );
                    }
                  } else {
                    progress = Math.min(1, item.viewedValue / item.totalValue);
                  }
                }
                const progressColor = isCompleted ? "#198754" : "#03bbfe";
                const showProgressBar = !isCompleted;
                return (
                  <Card
                    key={index}
                    className={`edu-watch-list-card-plain w-100 mb-2`}
                    style={{
                      position: "relative",
                      borderRadius: "20px",
                      border: isCurrent
                        ? `3px solid #1976d2`
                        : `2px solid ${borderColor}`,
                      boxShadow: isCurrent
                        ? "0 0 0 4px #1976d233"
                        : "0 0 10px 0 rgba(0,0,0,0.08)",
                      backgroundColor: isCurrent
                        ? isCompleted
                          ? "#c2e6d0"
                          : "#e0f3fd"
                        : cardColor,
                      transition: "all 0.2s",
                      cursor: isCurrent ? "default" : "pointer",
                      minHeight: 0,
                      padding: 0,
                      overflow: "hidden",
                    }}
                    onClick={() => {
                      if (!isCurrent) {
                        window.scrollTo(0, 0);
                        Search();
                        // 다른 영상으로 전환 시 모든 상태 즉시 초기화
                        sentEndRef.current = false; // 완료 플래그 초기화
                        actualPlayTimeRef.current = 0; // 실제 재생 시간 초기화
                        lastPlayTimeRef.current = 0; // 마지막 재생 시간 초기화
                        isPlayingRef.current = false; // 재생 상태 초기화
                        playStartTimeRef.current = null; // 재생 시작 시점 초기화
                        currentEduIdRef.current = item.eduId; // 현재 eduId 설정 (이전 콜백 차단용)
                        setSelectedData({ ...item });
                        setShouldSeekToStart(false);
                        setPlayedSeconds(0);
                        setVideoDuration(0);
                        setPdfInitialPage(1);
                        setSeekTarget(null); // seekTarget 초기화
                      } else if (isCompleted && playerRef.current) {
                        // 완료된 콘텐츠를 다시 클릭하면 처음부터 재생
                        console.log(
                          "[WatchEducation] Replaying completed video from start"
                        );
                        setSelectedData(prevData => ({ ...prevData, viewedValue: 0 }));
                        setShouldSeekToStart(true);
                      }
                    }}
                  >
                    {showProgressBar && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          height: "100%",
                          width: `${progress * 100}%`,
                          background: progressColor,
                          opacity: 0.18,
                          zIndex: 0,
                          transition: "width 0.3s",
                        }}
                      />
                    )}
                    <Card.Body
                      className="flex-between-center gap-2"
                      style={{
                        alignItems: "center",
                        padding: "0.45rem 0.7rem 0.45rem 0.7rem",
                        minHeight: 0,
                        background: "transparent",
                        zIndex: 1,
                      }}
                    >
                      <div
                        className="flex-center-center"
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 10,
                          background: "#f8f9fa",
                          border: `2px solid ${borderColor}`,
                          marginRight: 12,
                        }}
                      >
                        <FontAwesomeIcon
                          icon={icon}
                          style={{ color: statusColor, fontSize: 18 }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "1.05rem",
                            color: "#2c3e50",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <AutoTranslate targetLanguage={language.lang}>
                            {item.content.title}
                          </AutoTranslate>
                        </div>
                        <div
                          style={{
                            fontSize: "0.92rem",
                            color: "#6c757d",
                            marginTop: 2,
                          }}
                        >
                          {item.content.createdAt
                            ? item.content.createdAt.toString().slice(0, 10)
                            : ""}
                        </div>
                      </div>
                      <div
                        className="flex-column-center-center"
                        style={{ minWidth: 70, alignItems: "flex-end" }}
                      >
                        <span
                          style={{
                            fontWeight: 700,
                            color: statusColor,
                            fontSize: "1.02rem",
                          }}
                        >
                          {statusText}
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}
          </Card.Body>
          <Card.Footer style={{ background: "#fff", borderTop: "none" }}>
            <Button
              className="flex-center-center gap-2 w-100"
              onClick={() => navigate(-1)}
            >
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              <AutoTranslate targetLanguage={language.lang}>
                다른 교육 이수하러 가기
              </AutoTranslate>
            </Button>
          </Card.Footer>
        </Card>
      </div>
      {/* 전체화면 재생/정지 애니메이션 (Portal 사용) */}
      {showPlayPauseIcon &&
        isFullscreen &&
        ReactDOM.createPortal(
          <div
            className="play-pause-animation"
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "120px",
              height: "120px",
              background: "rgba(0, 0, 0, 0.7)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 999999,
              pointerEvents: "none",
              animation: "playPauseAnimation 0.5s ease-out",
            }}
          >
            <FontAwesomeIcon
              icon={playPauseIcon === "play" ? faPlay : faPause}
              style={{
                fontSize: "3.5rem",
                color: "#fff",
                marginLeft: playPauseIcon === "play" ? "8px" : "0",
              }}
            />
          </div>,
          document.body
        )}
    </div>
  );
}
export default WatchEducation;
